import React, { useState, useEffect, useMemo } from 'react';
import { usePreferences } from '../contexts/PreferencesContext';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Collapse,
  IconButton,
  LinearProgress,
  Alert,
  Button,
  Tooltip,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  ErrorOutlined as ErrorOutlineIcon,
  Speed as SpeedIcon,
  MonetizationOn as MonetizationOnIcon,
  ReportProblem as ReportProblemIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getMyTasks, getAwaitingApproval, getTaskStats, getProjectStats, getCompanyLabor, getLaborUtilization, getLaborUtilizationByPM, getExecutiveSummary, getTimelineGaps, getSalesFollowups, getUnlinkedMeetingsCount, getUnlinkedMeetings, linkMeeting, getProjects } from '../services/api';
import TimelineGapModal from '../components/TimelineGapModal';
import WipByDisciplineStrip from '../components/WipByDisciplineStrip';
import { format, isToday, isPast } from 'date-fns';

const ACCENT_COLORS = {
  primary: '#0D2C54', success: '#1B5E20', error: '#B71C1C', warning: '#E65100', info: '#0D47A1',
};
const StatCard = ({ title, value, sub, color = 'primary', onClick }) => {
  const accent = ACCENT_COLORS[color] || color;
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2.5, cursor: onClick ? 'pointer' : 'default',
        borderBottom: `3px solid ${accent}`,
        bgcolor: '#fff',
        transition: 'all 0.2s',
        '&:hover': onClick ? { boxShadow: '0 4px 20px rgba(0,0,0,0.08)', transform: 'translateY(-1px)' } : {},
      }}
    >
      <Typography variant="overline" sx={{ color: '#8898A4', letterSpacing: 1.2, fontSize: '0.65rem', lineHeight: 1 }}>
        {title}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color: accent, mt: 0.5, letterSpacing: '-0.5px' }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" sx={{ color: '#8898A4', mt: 0.3, display: 'block' }}>{sub}</Typography>}
    </Paper>
  );
};

const TaskItem = ({ task, onClick }) => {
  const isOverdue = task.due_date && isPast(new Date(task.due_date)) && !isToday(new Date(task.due_date));
  const isDueToday = task.due_date && isToday(new Date(task.due_date));

  return (
    <>
      <ListItem
        button
        onClick={onClick}
        sx={{
          '&:hover': { bgcolor: 'action.hover' },
          borderLeft: isOverdue ? '4px solid' : isDueToday ? '4px solid' : 'none',
          borderColor: isOverdue ? 'error.main' : 'warning.main',
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body1" fontWeight="medium">
                {task.name}
              </Typography>
              {isOverdue && (
                <Chip label="Overdue" size="small" color="error" />
              )}
              {isDueToday && (
                <Chip label="Due Today" size="small" color="warning" />
              )}
            </Box>
          }
          secondary={
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {task.project_name} • {task.phase_name}
              </Typography>
              {task.due_date && (
                <Typography variant="caption" color="text.secondary">
                  Due: {format(new Date(task.due_date), 'MMM d, yyyy')}
                </Typography>
              )}
            </Box>
          }
        />
      </ListItem>
      <Divider component="li" />
    </>
  );
};

function fmtCurrency(val) {
  if (val == null || val === '') return '$0';
  const num = parseFloat(val);
  if (isNaN(num) || num === 0) return '$0';
  if (num >= 1_000_000) return '$' + (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 1_000) return '$' + (num / 1_000).toFixed(0) + 'K';
  return '$' + num.toLocaleString();
}

// ─── Fiscal Year Dashboard (Management) ───────────────────────────────────────

function fmtCurrencyFull(val) {
  if (val == null || isNaN(val) || val === 0) return '$0';
  const num = parseFloat(val);
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

const FiscalYearDashboard = ({ fiscalYears, navigate }) => {
  // Default to first entry (current FY)
  const [selectedTab, setSelectedTab] = useState(0);
  const [fySortBy, setFySortBy] = useState('');
  const [fySortDir, setFySortDir] = useState('asc');

  const handleFySort = (col) => {
    if (fySortBy === col) {
      if (fySortDir === 'asc') setFySortDir('desc');
      else { setFySortBy(''); setFySortDir('asc'); }
    } else {
      setFySortBy(col);
      setFySortDir('asc');
    }
  };

  const sortFyData = (data) => {
    if (!fySortBy) return data;
    return [...data].sort((a, b) => {
      let aVal = a[fySortBy] ?? '';
      let bVal = b[fySortBy] ?? '';
      if (typeof aVal === 'number' || (!isNaN(parseFloat(aVal)) && aVal !== '')) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return fySortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return fySortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  if (!fiscalYears || fiscalYears.length === 0) return null;

  const fy = fiscalYears[selectedTab];

  return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} onClick={() => navigate('/sales-pipeline')} sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}>
          Fiscal Year Financial Overview →
        </Typography>
        <Tabs
          value={selectedTab}
          onChange={(e, v) => setSelectedTab(v)}
          size="small"
          sx={{ minHeight: 36 }}
        >
          {fiscalYears.map((f, i) => (
            <Tab key={f.fy} label={f.fy} sx={{ minHeight: 36, py: 0.5 }} />
          ))}
        </Tabs>
      </Box>

      {/* KPI cards for selected FY */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Box onClick={() => navigate('/sales-pipeline')} sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.100', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              {fmtCurrency(fy.total_pipeline)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Total Pipeline
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box onClick={() => navigate('/projects')} sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.100', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
            <Typography variant="h5" fontWeight="bold" color="success.main">
              {fmtCurrency(fy.closed_won)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Closed Won
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box onClick={() => navigate('/sales-pipeline')} sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.100', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
            <Typography variant="h5" fontWeight="bold" color="info.main">
              {fmtCurrency(fy.avg_deal_size)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Avg Deal Size
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box onClick={() => navigate('/sales-pipeline')} sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
            <Typography variant="h5" fontWeight="bold">
              {fy.total_deals}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Total Deals
              {fy.closed_won_count > 0 && (
                <Typography component="span" variant="caption" color="success.main" sx={{ ml: 0.5 }}>
                  ({fy.closed_won_count} won)
                </Typography>
              )}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* By-salesperson table */}
      {fy.by_salesman && fy.by_salesman.length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
            Closed Won by Salesperson — {fy.fy}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  {[
                    { key: 'salesman',   label: 'Salesperson', align: 'left' },
                    { key: 'pipeline',   label: 'Pipeline',    align: 'right' },
                    { key: 'closed_won', label: 'Closed Won',  align: 'right' },
                    { key: 'deals',      label: '# Deals',     align: 'right' },
                    { key: 'win_rate',   label: 'Win Rate',    align: 'right' },
                  ].map(col => (
                    <TableCell key={col.key} align={col.align} sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={fySortBy === col.key}
                        direction={fySortBy === col.key ? fySortDir : 'asc'}
                        onClick={() => handleFySort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortFyData(fy.by_salesman.map(row => ({
                  ...row,
                  win_rate: row.deals > 0 ? Math.round((row.closed_won / (row.pipeline || 1)) * 100) : 0,
                }))).map((row) => (
                  <TableRow key={row.salesman} hover>
                    <TableCell>{row.salesman}</TableCell>
                    <TableCell align="right">{fmtCurrencyFull(row.pipeline)}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: row.closed_won > 0 ? 'bold' : 'normal' }}>
                      {fmtCurrencyFull(row.closed_won)}
                    </TableCell>
                    <TableCell align="right">{row.deals}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${row.win_rate}%`}
                        size="small"
                        color={row.win_rate >= 50 ? 'success' : row.win_rate >= 25 ? 'warning' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
};

// ─── Company Labor Card (Management only) ─────────────────────────────────────

const CompanyLaborCard = () => {
  const navigate = useNavigate();
  const [labor, setLabor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [laborSortBy, setLaborSortBy] = useState('');
  const [laborSortDir, setLaborSortDir] = useState('asc');

  const handleLaborSort = (col) => {
    if (laborSortBy === col) {
      if (laborSortDir === 'asc') setLaborSortDir('desc');
      else { setLaborSortBy(''); setLaborSortDir('asc'); }
    } else {
      setLaborSortBy(col);
      setLaborSortDir('asc');
    }
  };

  const sortLaborData = (data) => {
    if (!laborSortBy) return data;
    return [...data].sort((a, b) => {
      let aVal = a[laborSortBy] ?? '';
      let bVal = b[laborSortBy] ?? '';
      if (typeof aVal === 'number' || (!isNaN(parseFloat(aVal)) && aVal !== '')) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return laborSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return laborSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  useEffect(() => {
    getCompanyLabor()
      .then((res) => setLabor(res.data))
      .catch(() => setLabor(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2, mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} gutterBottom onClick={() => navigate('/labor-utilization')} sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}>Company Labor →</Typography>
      <CircularProgress size={24} />
    </Paper>
  );

  if (!labor) return null;

  const periodLabel = labor.pay_period_start && labor.pay_period_end
    ? `${format(new Date(labor.pay_period_start), 'MMM d')} – ${format(new Date(labor.pay_period_end), 'MMM d, yyyy')}`
    : 'Latest Pay Period';

  return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} onClick={() => navigate('/labor-utilization')} sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}>
          Company Labor →
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {periodLabel}
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.100' }}>
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              {labor.total_hours.toFixed(1)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Total Hours
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.100' }}>
            <Typography variant="h5" fontWeight="bold" color="success.main">
              {labor.billable_hours.toFixed(1)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Billable Hours
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50', borderRadius: 2, border: '1px solid', borderColor: 'warning.100' }}>
            <Typography variant="h5" fontWeight="bold" color="warning.main">
              {labor.unbillable_hours.toFixed(1)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Unbillable Hours
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.100' }}>
            <Typography variant="h5" fontWeight="bold" color={
              labor.utilization_rate >= 75 ? 'success.main'
              : labor.utilization_rate >= 50 ? 'warning.main'
              : 'error.main'
            }>
              {labor.utilization_rate}%
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Utilization Rate
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {labor.by_employee && labor.by_employee.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                {[
                  { key: 'employee_name',    label: 'Employee',   align: 'left' },
                  { key: 'total_hours',      label: 'Total',      align: 'right' },
                  { key: 'billable_hours',   label: 'Billable',   align: 'right' },
                  { key: 'unbillable_hours', label: 'Unbillable', align: 'right' },
                  { key: 'utilization',      label: 'Utilization',align: 'right' },
                ].map(col => (
                  <TableCell key={col.key} align={col.align} sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={laborSortBy === col.key}
                      direction={laborSortBy === col.key ? laborSortDir : 'asc'}
                      onClick={() => handleLaborSort(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortLaborData(labor.by_employee.map(emp => ({
                ...emp,
                utilization: emp.total_hours > 0 ? Math.round((emp.billable_hours / emp.total_hours) * 100) : 0,
              }))).map((emp) => (
                <TableRow key={emp.employee_name} hover>
                  <TableCell>{emp.employee_name}</TableCell>
                  <TableCell align="right">{emp.total_hours.toFixed(1)}</TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>{emp.billable_hours.toFixed(1)}</TableCell>
                  <TableCell align="right" sx={{ color: 'warning.main' }}>{emp.unbillable_hours.toFixed(1)}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${emp.utilization}%`}
                      size="small"
                      color={emp.utilization >= 75 ? 'success' : emp.utilization >= 50 ? 'warning' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

// ─── Util Helpers ─────────────────────────────────────────────────────────────

function fmtHours(val) {
  const n = parseFloat(val || 0);
  if (isNaN(n)) return '0';
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
}

function utilColor(pct) {
  if (pct > 100) return '#d32f2f';   // red
  if (pct > 80)  return '#f57c00';   // orange/yellow
  return '#388e3c';                   // green
}

function utilBgColor(pct) {
  if (pct > 100) return '#ffebee';
  if (pct > 80)  return '#fff8e1';
  return '#e8f5e9';
}

// ─── Utilization Bar ──────────────────────────────────────────────────────────
const UtilBar = ({ pct }) => {
  const capped   = Math.min(pct, 100);
  const overflow = pct > 100 ? Math.min(pct - 100, 50) : 0; // visual overflow portion
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <Box sx={{ flex: 1, position: 'relative', height: 8, bgcolor: 'grey.200', borderRadius: 4, overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${capped}%`,
            bgcolor: utilColor(pct),
            borderRadius: 4,
            transition: 'width 0.4s ease',
          }}
        />
        {overflow > 0 && (
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              height: '100%',
              width: `${Math.min(overflow * 0.5, 30)}%`,
              bgcolor: '#b71c1c',
              borderRadius: 4,
            }}
          />
        )}
      </Box>
      <Typography
        variant="caption"
        fontWeight="bold"
        sx={{ color: utilColor(pct), minWidth: 42, textAlign: 'right' }}
      >
        {pct.toFixed(1)}%
      </Typography>
    </Box>
  );
};

// ─── PM Job Detail Row ────────────────────────────────────────────────────────
const PMJobsDetail = ({ pm }) => {
  const [jobs, setJobs]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState(null);

  React.useEffect(() => {
    getLaborUtilizationByPM(pm)
      .then((res) => setJobs(res.data.jobs || []))
      .catch((err) => setError(err.response?.data?.error || 'Failed to load job data'))
      .finally(() => setLoading(false));
  }, [pm]);

  if (loading) return (
    <TableRow>
      <TableCell colSpan={8} sx={{ py: 2, pl: 6, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">Loading jobs…</Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  if (error) return (
    <TableRow>
      <TableCell colSpan={8} sx={{ py: 1, pl: 6, bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="error">{error}</Typography>
      </TableCell>
    </TableRow>
  );

  if (!jobs || jobs.length === 0) return (
    <TableRow>
      <TableCell colSpan={8} sx={{ py: 1, pl: 6, bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary">No open jobs found for this PM.</Typography>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      {jobs.map((job) => (
        <TableRow key={job.job_number} sx={{ bgcolor: 'grey.50', '&:hover': { bgcolor: 'grey.100' } }}>
          <TableCell sx={{ pl: 6 }}>
            <Typography variant="caption" color="text.secondary" fontStyle="italic">
              {job.job_number}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="caption">{job.description}</Typography>
          </TableCell>
          <TableCell align="right">
            <Typography variant="caption">{fmtHours(job.est_hours)}</Typography>
          </TableCell>
          <TableCell align="right">
            <Typography variant="caption">{fmtHours(job.actual_hours)}</Typography>
          </TableCell>
          <TableCell>
            <UtilBar pct={job.utilization_pct} />
          </TableCell>
          <TableCell align="right">
            <Typography variant="caption">{fmtCurrency(job.est_cost)}</Typography>
          </TableCell>
          <TableCell align="right">
            <Typography variant="caption">{fmtCurrency(job.actual_cost)}</Typography>
          </TableCell>
          <TableCell align="right">
            <Typography variant="caption">{fmtCurrency(job.contract_value)}</Typography>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
};

// ─── Labor Utilization Report (Management only) ───────────────────────────────

const LaborUtilizationReport = () => {
  const navigate = useNavigate();
  const [data, setData]         = React.useState(null);
  const [loading, setLoading]   = React.useState(true);
  const [error, setError]       = React.useState(null);
  const [expandedPM, setExpandedPM] = React.useState(null);
  const [sortBy, setSortBy]     = React.useState('actual_hours');
  const [sortDir, setSortDir]   = React.useState('desc');

  React.useEffect(() => {
    getLaborUtilization()
      .then((res) => setData(res.data))
      .catch((err) => {
        const msg = err.response?.data?.error || err.message || 'Failed to load labor utilization';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const sortedRows = React.useMemo(() => {
    if (!data?.by_pm) return [];
    return [...data.by_pm].sort((a, b) => {
      let av = a[sortBy] ?? 0;
      let bv = b[sortBy] ?? 0;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortBy, sortDir]);

  const handleRowClick = (pm) => {
    setExpandedPM((prev) => (prev === pm ? null : pm));
  };

  if (loading) return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2, mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} gutterBottom onClick={() => navigate("/labor-utilization")} sx={{ cursor: "pointer", "&:hover": { color: "primary.main", textDecoration: "underline" } }}>
          WIP Labor Utilization →
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">Loading live Sage data…</Typography>
      </Box>
    </Paper>
  );

  if (error) return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2, mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} gutterBottom onClick={() => navigate("/labor-utilization")} sx={{ cursor: "pointer", "&:hover": { color: "primary.main", textDecoration: "underline" } }}>
          WIP Labor Utilization →
      </Typography>
      <Alert severity="warning" sx={{ mt: 1 }}>
        {error} — Sage may be temporarily unreachable. Try refreshing in a moment.
      </Alert>
    </Paper>
  );

  if (!data) return null;

  const { summary } = data;
  const overallPct  = summary.overall_util_pct || 0;

  const COLS = [
    { key: 'pm',              label: 'Project Manager', align: 'left' },
    { key: 'job_count',       label: 'Jobs',            align: 'right' },
    { key: 'est_hours',       label: 'Est Hours',       align: 'right' },
    { key: 'actual_hours',    label: 'Actual Hours',    align: 'right' },
    { key: 'utilization_pct', label: 'Utilization %',   align: 'left' },
    { key: 'est_cost',        label: 'Est Cost',        align: 'right' },
    { key: 'actual_cost',     label: 'Actual Cost',     align: 'right' },
    { key: 'contract_value',  label: 'Contract Value',  align: 'right' },
  ];

  return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} onClick={() => navigate('/labor-utilization')} sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}>
          WIP Labor Utilization →
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Live from Sage — Open jobs only
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.100' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
              <AccessTimeIcon fontSize="small" color="primary" />
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                {fmtHours(summary.total_est_hours)}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Total WIP Est Hours
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.100' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
              <AccessTimeIcon fontSize="small" color="info" />
              <Typography variant="h5" fontWeight="bold" color="info.main">
                {fmtHours(summary.total_actual_hours)}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Total Actual Hours
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{
            textAlign: 'center', p: 2, borderRadius: 2, border: '1px solid',
            bgcolor: utilBgColor(overallPct),
            borderColor: utilColor(overallPct) + '40',
          }}>
            <Typography variant="h5" fontWeight="bold" sx={{ color: utilColor(overallPct), mb: 0.5 }}>
              {overallPct.toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Overall Utilization
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(overallPct, 100)}
              sx={{
                mt: 1, height: 4, borderRadius: 2,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': { bgcolor: utilColor(overallPct) },
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50', borderRadius: 2, border: '1px solid', borderColor: 'warning.100' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
              <AttachMoneyIcon fontSize="small" color="warning" />
              <Typography variant="h5" fontWeight="bold" color="warning.main">
                {fmtCurrency(summary.total_actual_cost)}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Total Labor Cost (JTD)
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* PM Utilization Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ width: 40 }} />
              {COLS.map((col) => (
                <TableCell key={col.key} align={col.align} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={sortBy === col.key}
                    direction={sortBy === col.key ? sortDir : 'asc'}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row) => {
              const isExpanded = expandedPM === row.pm;
              return (
                <React.Fragment key={row.pm}>
                  <TableRow
                    hover
                    onClick={() => handleRowClick(row.pm)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isExpanded ? 'primary.50' : 'inherit',
                      '&:hover': { bgcolor: isExpanded ? 'primary.100' : 'action.hover' },
                    }}
                  >
                    <TableCell sx={{ width: 40, py: 0.5 }}>
                      <IconButton size="small" sx={{ p: 0.5 }}>
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>{row.pm}</TableCell>
                    <TableCell align="right">
                      <Chip label={row.job_count} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{fmtHours(row.est_hours)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmtHours(row.actual_hours)}</TableCell>
                    <TableCell>
                      <UtilBar pct={row.utilization_pct} />
                    </TableCell>
                    <TableCell align="right">{fmtCurrency(row.est_cost)}</TableCell>
                    <TableCell align="right" sx={{ color: row.actual_cost > row.est_cost ? 'error.main' : 'inherit' }}>
                      {fmtCurrency(row.actual_cost)}
                    </TableCell>
                    <TableCell align="right">{fmtCurrency(row.contract_value)}</TableCell>
                  </TableRow>

                  {/* Expanded job detail rows */}
                  {isExpanded && (
                    <>
                      <TableRow>
                        <TableCell colSpan={9} sx={{ p: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.100' }}>
                                  <TableCell sx={{ pl: 6, fontWeight: 'bold', fontSize: '0.72rem' }}>Job #</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.72rem' }}>Description</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.72rem' }}>Est Hrs</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.72rem' }}>Act Hrs</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.72rem' }}>Utilization</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.72rem' }}>Est Cost</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.72rem' }}>Act Cost</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.72rem' }}>Contract</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <PMJobsDetail pm={row.pm} />
                              </TableBody>
                            </Table>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {sortedRows.length === 0 && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No utilization data available.</Typography>
        </Box>
      )}
    </Paper>
  );
};

// ─── Executive Dashboard (Management only) ────────────────────────────────────

const FLAG_LABELS = {
  labor_overrun:   { label: 'Labor Overrun',   color: 'error'   },
  no_billing_45d:  { label: 'No Bill 45d',      color: 'warning' },
  no_pm:           { label: 'No PM',            color: 'default' },
  past_completion: { label: 'Past Completion',  color: 'warning' },
  negative_margin: { label: 'Negative Margin',  color: 'error'   },
};

const FlagChip = ({ flag }) => {
  const def = FLAG_LABELS[flag] || { label: flag.replace(/_/g, ' '), color: 'default' };
  return (
    <Chip
      label={def.label}
      size="small"
      color={def.color}
      variant="outlined"
      sx={{ fontSize: '0.68rem', height: 20 }}
    />
  );
};

const ExecutiveDashboard = ({ execSummary, navigate, isExec = false }) => {
  // ── Watchlist sort state ──
  const [wlSortBy, setWlSortBy]   = useState('labor_pct');
  const [wlSortDir, setWlSortDir] = useState('desc');

  const handleWlSort = (col) => {
    if (wlSortBy === col) setWlSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setWlSortBy(col); setWlSortDir('desc'); }
  };

  const {
    active_projects = 0,
    total_wip_value = 0,
    total_pipeline = 0,
    open_ar = 0,
    labor_utilization_pct = 0,
    revenue_this_month = 0,
    at_risk_count = 0,
    ar_aging = {},
    top_ar_balances = [],
    cash_flow_alerts = [],
    labor_overruns = [],
    watchlist = [],
    revenue_by_year = [],
    sage_ar = null,
  } = execSummary || {};

  // Use Sage ARM data (Pat's report) as source of truth when available
  const effectiveArAging = sage_ar ? {
    current: sage_ar.current,
    days_30: 0, // ARM doesn't have 1-30 separate from current
    days_60: sage_ar.days_31_60,
    days_90: sage_ar.days_61_90,
    days_120: sage_ar.days_91_120,
    days_120_plus: sage_ar.over_120,
  } : ar_aging;
  const effectiveTopAr = sage_ar ? sage_ar.top_balances.map(r => ({
    client: r.name,
    project_name: r.customer_id,
    amount: r.outstanding,
    days_outstanding: r.avg_days_to_pay,
    project_id: null,
  })) : top_ar_balances;

  const sortedWatchlist = useMemo(() => {
    if (!watchlist.length) return [];
    return [...watchlist].sort((a, b) => {
      let av = a[wlSortBy] ?? 0;
      let bv = b[wlSortBy] ?? 0;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return wlSortDir === 'asc' ? -1 : 1;
      if (av > bv) return wlSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [watchlist, wlSortBy, wlSortDir]);

  // ── AR aging bar ──
  const agingBuckets = [
    { key: 'current',      label: 'Current', color: '#4CAF50' },
    { key: 'days_30',      label: '1-30',    color: '#FFC107' },
    { key: 'days_60',      label: '31-60',   color: '#FF9800' },
    { key: 'days_90',      label: '61-90',   color: '#F44336' },
    { key: 'days_120',     label: '91-120',  color: '#B71C1C' },
    { key: 'days_120_plus',label: '120+',    color: '#880E4F' },
  ];
  const agingTotal = agingBuckets.reduce((sum, b) => sum + (parseFloat(effectiveArAging[b.key]) || 0), 0);

  // ── Cash flow totals ──
  const totalAtRisk = cash_flow_alerts.reduce((sum, a) => sum + (parseFloat(a.gap_amount) || 0), 0);

  // ── KPI color helpers ──
  const arColor    = open_ar > 500000 ? 'error' : open_ar > 100000 ? 'warning' : 'success';
  const laborColor = labor_utilization_pct < 80 ? 'success' : labor_utilization_pct <= 100 ? 'warning' : 'error';
  const riskColor  = at_risk_count > 10 ? 'error' : at_risk_count > 5 ? 'warning' : 'success';

  return (
    <>
      {/* ── KPI Strip ── */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap', py: 1 }}>
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <StatCard title="Active Projects" value={active_projects} color="primary" onClick={() => navigate('/projects')} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <StatCard title="WIP Value" value={fmtCurrency(total_wip_value)} color="success" onClick={() => navigate('/projects')} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <StatCard title="Pipeline" value={fmtCurrency(total_pipeline)} color="info" onClick={() => navigate('/sales-pipeline')} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <StatCard title="Open AR" value={fmtCurrency(open_ar)} color={arColor} onClick={() => navigate('/ar-report')} />
        </Box>
        {/* 2026-07-23 Chris G: renamed "Revenue MTD" → "Collected This Month",
            moved right of Open AR; Labor Utilization pushed to far right. */}
        {isExec && (
          <Box sx={{ flex: 1, minWidth: 140 }}>
            <StatCard title="Collected This Month" value={fmtCurrency(revenue_this_month)} color="primary" onClick={() => navigate('/ar-report')} />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <StatCard title="At Risk" value={at_risk_count} color={riskColor} onClick={() => navigate('/calendar')} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <StatCard title="Labor Utilization" value={`${labor_utilization_pct}%`} color={laborColor} onClick={() => navigate('/labor-utilization')} />
        </Box>
      </Box>

      {/* ── WIP by Discipline (live rollup) ──
           Jonathan Flamm's July 2026 ask: break the single WIP tile above into
           per-discipline detail (AV / LTG / SHD / IT / HT) plus WIP-to-
           Complete. Data ties out to Pat Kross's WIP_to_Complete_for_CC.xlsx
           (2026-07-15) on Contract, Estimate, Billed — dollar for dollar. */}
      {isExec && <WipByDisciplineStrip />}

      {/* ── 2. AR Aging (~60%) + Cash Flow Alerts (~40%) ── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>

        {/* Left: AR Aging Snapshot */}
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} onClick={() => navigate('/ar-report')} sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}>
                AR Aging Snapshot →
              </Typography>

            </Box>

            {agingTotal === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                <Typography color="text.secondary">No outstanding AR</Typography>
              </Box>
            ) : (
              <>
                {/* Total */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Total Outstanding: <strong>{fmtCurrencyFull(agingTotal)}</strong>
                </Typography>

                {/* Stacked color bar */}
                <Box sx={{ display: 'flex', height: 20, borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                  {agingBuckets.map((b) => {
                    const val = parseFloat(effectiveArAging[b.key]) || 0;
                    const pct = agingTotal > 0 ? (val / agingTotal) * 100 : 0;
                    if (pct < 0.5) return null;
                    return (
                      <Tooltip key={b.key} title={`${b.label}: ${fmtCurrencyFull(val)}`} arrow>
                        <Box sx={{ width: `${pct}%`, bgcolor: b.color, cursor: 'default', transition: 'width 0.4s' }} />
                      </Tooltip>
                    );
                  })}
                </Box>

                {/* Legend row */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                  {agingBuckets.map((b) => {
                    const val = parseFloat(effectiveArAging[b.key]) || 0;
                    if (val === 0) return null;
                    return (
                      <Box key={b.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, bgcolor: b.color, borderRadius: '50%', flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary">
                          {b.label}: <strong>{fmtCurrency(val)}</strong>
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>

                {/* Top AR balances */}
                {effectiveTopAr.length > 0 && (
                  <>
                    <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                      Largest Outstanding Balances
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Client / Project</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Balance</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Days Outstanding</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {effectiveTopAr.slice(0, 5).map((row, i) => (
                            <TableRow
                              key={i}
                              hover
                              sx={{ cursor: row.project_id ? 'pointer' : 'default' }}
                              onClick={() => row.project_id && navigate('/projects/' + row.project_id)}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">{row.client}</Typography>
                                {row.project_name && row.project_name !== row.client && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {row.project_name}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', color: (row.days_outstanding || 0) > 60 ? 'error.main' : 'inherit' }}>
                                {fmtCurrencyFull(row.amount)}
                              </TableCell>
                              <TableCell align="right">
                                {row.days_outstanding != null ? (
                                  <Chip
                                    label={`${row.days_outstanding}d`}
                                    size="small"
                                    color={row.days_outstanding > 90 ? 'error' : row.days_outstanding > 60 ? 'warning' : 'default'}
                                    variant="outlined"
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">—</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )}
          </Paper>
        </Grid>

        {/* Right: Cash Flow Alerts */}
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} onClick={() => navigate('/ar-report')} sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}>
                Cash Flow Alerts →
              </Typography>
              {cash_flow_alerts.length > 0 && (
                <Chip label={cash_flow_alerts.length} size="small" color="warning" sx={{ ml: 'auto' }} />
              )}
            </Box>

            {cash_flow_alerts.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                <Typography color="text.secondary">No cash flow alerts</Typography>
              </Box>
            ) : (
              <>
                <List dense disablePadding sx={{ maxHeight: 360, overflow: 'auto' }}>
                  {cash_flow_alerts.map((item, i) => (
                    <React.Fragment key={i}>
                      <ListItem
                        disableGutters
                        sx={{ py: 0.75, cursor: item.project_id ? 'pointer' : 'default', '&:hover': { bgcolor: item.project_id ? 'action.hover' : 'transparent' }, borderRadius: 1 }}
                        onClick={() => item.project_id && navigate('/projects/' + item.project_id)}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: '62%' }}>
                                {item.name}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold" color="error.main">
                                {fmtCurrency(item.gap_amount)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {item.job_number} · Labor {item.labor_pct}% vs Billed {item.billed_pct}%
                            </Typography>
                          }
                        />
                      </ListItem>
                      {i < cash_flow_alerts.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Total $ at risk</Typography>
                  <Typography variant="body2" fontWeight="bold" color="error.main">
                    {fmtCurrencyFull(totalAtRisk)}
                  </Typography>
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ── 3. Labor & Margin Risk Row ── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>

        {/* Left: Worst Labor Overruns (top 10) */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} onClick={() => navigate('/projects')} sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}>
                Worst Labor Overruns →
              </Typography>
              {labor_overruns.length > 0 && (
                <Chip label={`Top ${labor_overruns.length}`} size="small" color="error" variant="outlined" sx={{ ml: 'auto' }} />
              )}
            </Box>
            {labor_overruns.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                <Typography color="text.secondary">No labor overruns detected</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Project</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>PM</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Est</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Act</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>% Over</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>$ Impact</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {labor_overruns.map((item, i) => {
                      const pct = parseFloat(item.pct_over || 0);
                      const rowBg = pct > 100 ? '#ffebee' : pct > 90 ? '#fff8e1' : '#fffde7';
                      return (
                        <TableRow
                          key={i}
                          hover
                          sx={{ bgcolor: rowBg, cursor: 'pointer' }}
                          onClick={() => item.project_id && navigate('/projects/' + item.project_id)}
                        >
                          <TableCell>
                            <Tooltip title={`${item.name} — ${item.job_number}`} arrow>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 130 }}>{item.name}</Typography>
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary" display="block">{item.job_number}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 80 }}>{item.pm || '—'}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="caption">{fmtHours(item.est_hours)}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                            <Typography variant="body2">{fmtHours(item.actual_hours)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${pct.toFixed(0)}%`}
                              size="small"
                              color={pct > 100 ? 'error' : 'warning'}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                            <Typography variant="body2">{fmtCurrency(item.impact_dollars)}</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Right: Employee Utilization Concerns — pulls from CompanyLaborCard data */}
        <Grid item xs={12} md={6}>
          <EmployeeUtilizationPanel navigate={navigate} />
        </Grid>
      </Grid>

      {/* ── 4. Project Health Watchlist (full width) ── */}
      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} onClick={() => navigate('/calendar')} sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}>
            Project Health Watchlist →
          </Typography>
          {watchlist.length > 0 && (
            <Chip
              label={`${watchlist.length} project${watchlist.length !== 1 ? 's' : ''} need attention`}
              size="small"
              color="warning"
              sx={{ ml: 1 }}
            />
          )}
        </Box>

        {watchlist.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
            <Typography color="text.secondary">All projects are on track — no flags detected</Typography>
          </Box>
        ) : (<>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  {[
                    { key: 'name',               label: 'Project',      align: 'left'  },
                    { key: 'job_number',          label: 'Job#',         align: 'left'  },
                    { key: 'pm',                  label: 'PM',           align: 'left'  },
                    { key: 'phase',               label: 'Phase',        align: 'right' },
                    { key: 'labor_pct',           label: 'Labor %',      align: 'right' },
                    { key: 'billed',              label: 'Billed',       align: 'right' },
                    { key: 'cost',                label: 'Cost',         align: 'right' },
                    { key: 'days_since_last_bill',label: 'Days No Bill', align: 'right' },
                    { key: null,                  label: 'Flags',        align: 'left'  },
                  ].map((col, ci) => (
                    <TableCell key={ci} align={col.align} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      {col.key ? (
                        <TableSortLabel
                          active={wlSortBy === col.key}
                          direction={wlSortBy === col.key ? wlSortDir : 'asc'}
                          onClick={() => handleWlSort(col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedWatchlist.slice(0, 10).map((item, i) => (
                  <TableRow
                    key={i}
                    hover
                    sx={{ cursor: item.project_id ? 'pointer' : 'default' }}
                    onClick={() => item.project_id && navigate('/projects/' + item.project_id)}
                  >
                    <TableCell sx={{ maxWidth: 180 }}>
                      <Typography variant="body2" noWrap fontWeight="medium">{item.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{item.job_number || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.pm || '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={`Ph ${item.phase}`} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ color: item.labor_pct > 100 ? 'error.main' : item.labor_pct > 90 ? 'warning.main' : item.labor_pct > 80 ? '#f57c00' : 'success.main' }}
                      >
                        {item.labor_pct.toFixed(1)}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{fmtCurrency(item.billed)}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ color: item.cost > item.billed ? 'error.main' : 'inherit', fontWeight: item.cost > item.billed ? 'bold' : 'normal' }}>
                      <Typography variant="body2">{fmtCurrency(item.cost)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {item.days_since_last_bill != null ? (
                        <Chip
                          label={`${item.days_since_last_bill}d`}
                          size="small"
                          color={item.days_since_last_bill >= 90 ? 'error' : item.days_since_last_bill >= 45 ? 'warning' : 'default'}
                          variant="outlined"
                        />
                      ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(item.flag_reasons || []).map((flag, fi) => (
                          <FlagChip key={fi} flag={flag} />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {sortedWatchlist.length > 10 && (
            <Box sx={{ mt: 1.5, textAlign: 'center' }}>
              <Button size="small" onClick={() => navigate('/calendar')} sx={{ textTransform: 'none', color: '#8898A4' }}>
                View all {sortedWatchlist.length} projects →
              </Button>
            </Box>
          )}
        </>)}
      </Paper>
    </>
  );
};

// ─── Employee Utilization Panel ───────────────────────────────────────────────
// Reuses the existing /labor/summary endpoint — shows employees sorted by utilization concern

const EmployeeUtilizationPanel = ({ navigate }) => {
  const [labor, setLabor]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCompanyLabor()
      .then((res) => setLabor(res.data))
      .catch(() => setLabor(null))
      .finally(() => setLoading(false));
  }, []);

  const employees = useMemo(() => {
    if (!labor?.by_employee) return [];
    return [...labor.by_employee]
      .map((emp) => ({
        ...emp,
        utilization: emp.total_hours > 0
          ? Math.round((emp.billable_hours / emp.total_hours) * 100)
          : 0,
      }))
      .sort((a, b) => {
        // Sort: most over-utilized first, then most under-utilized
        const aRisk = a.utilization > 100 ? a.utilization - 100 : a.utilization < 60 ? 60 - a.utilization : 0;
        const bRisk = b.utilization > 100 ? b.utilization - 100 : b.utilization < 60 ? 60 - b.utilization : 0;
        return bRisk - aRisk;
      });
  }, [labor]);

  const periodLabel = labor?.pay_period_start && labor?.pay_period_end
    ? `${format(new Date(labor.pay_period_start), 'MMM d')} – ${format(new Date(labor.pay_period_end), 'MMM d')}`
    : 'Latest Pay Period';

  return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon color="primary" fontSize="small" />
          <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} onClick={() => navigate("/labor-utilization")} sx={{ cursor: "pointer", "&:hover": { color: "primary.main", textDecoration: "underline" } }}>
            Employee Utilization Concerns →
          </Typography>
        </Box>
        {!loading && labor && (
          <Typography variant="caption" color="text.secondary">{periodLabel}</Typography>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="caption" color="text.secondary">Loading…</Typography>
        </Box>
      )}

      {!loading && !labor && (
        <Alert severity="warning" sx={{ mt: 1 }}>Could not load labor data</Alert>
      )}

      {!loading && labor && (
        <>
          {/* Summary row */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'primary.50', borderRadius: 1, flex: 1, border: '1px solid', borderColor: 'primary.100' }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} color="primary.main">{labor.total_hours?.toFixed(0)}</Typography>
              <Typography variant="caption" color="text.secondary">Total Hrs</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'success.50', borderRadius: 1, flex: 1, border: '1px solid', borderColor: 'success.100' }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} color="success.main">{labor.billable_hours?.toFixed(0)}</Typography>
              <Typography variant="caption" color="text.secondary">Billable</Typography>
            </Box>
            <Box sx={{
              textAlign: 'center', p: 1.5, borderRadius: 1, flex: 1, border: '1px solid',
              bgcolor: utilBgColor(labor.utilization_rate),
              borderColor: utilColor(labor.utilization_rate) + '40',
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} sx={{ color: utilColor(labor.utilization_rate) }}>
                {labor.utilization_rate}%
              </Typography>
              <Typography variant="caption" color="text.secondary">Util Rate</Typography>
            </Box>
          </Box>

          {employees.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No employee data available</Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 280, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>Employee</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>Billable</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>Util %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((emp) => {
                    const isOverUtil  = emp.utilization > 100;
                    const isUnderUtil = emp.utilization < 60;
                    return (
                      <TableRow key={emp.employee_name} hover sx={{ bgcolor: isOverUtil ? '#ffebee' : isUnderUtil ? '#fff8e1' : 'inherit' }}>
                        <TableCell>
                          <Typography variant="body2">{emp.employee_name}</Typography>
                          {isOverUtil && (
                            <Typography variant="caption" color="error.main">Over-utilized</Typography>
                          )}
                          {isUnderUtil && !isOverUtil && (
                            <Typography variant="caption" color="warning.main">Under 60% billable</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{emp.total_hours?.toFixed(1)}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'success.main' }}>
                          <Typography variant="body2">{emp.billable_hours?.toFixed(1)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${emp.utilization}%`}
                            size="small"
                            color={isOverUtil ? 'error' : isUnderUtil ? 'warning' : 'success'}
                            variant="outlined"
                            sx={{ fontSize: '0.72rem' }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [myTasks, setMyTasks] = useState([]);
  const [awaitingApproval, setAwaitingApproval] = useState([]);
  const [taskStats, setTaskStats] = useState({});
  const [projectStats, setProjectStats] = useState({});
  const [execSummary, setExecSummary] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [execError, setExecError] = useState(false);
  const [timelineGaps, setTimelineGaps] = useState(null);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [salesFollowups, setSalesFollowups] = useState(null);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const { user } = useAuth();
  const { preferences } = usePreferences();
  const navigate = useNavigate();
  const isMgmt = user?.role === 'Management' || user?.roles?.includes('Management');
  // Executive Management: Robert (1), Jonathan (2), Tom (3), Chris G (4), Pat (19), Jeanne (90), Joe Calabrese (93 — added 2026-07-30).
  // Keep in sync with EXEC_MGMT_IDS in Layout.js.
  const EXEC_IDS = new Set([1, 2, 3, 4, 19, 90, 93]);
  const isExec = EXEC_IDS.has(user?.id);

  // Roles that see the timeline gap modal (not Management)
  // Sales added 2026-07-20 (Tom Clancy): Sales reps own dates on jobs they
  // sold and can edit them via the same modal; they need to see the nag too
  // during WIP cleanup so both sides of the aisle push the same dates.
  const isTimelineRole = user?.role === 'PM' || user?.role === 'Operations' || user?.role === 'Field Operations' || user?.role === 'Sales'
    || (Array.isArray(user?.roles) && (user.roles.includes('PM') || user.roles.includes('Operations') || user.roles.includes('Field Operations') || user.roles.includes('Sales')));
  // Sales follow-ups apply to ANYONE who is a sales rep on any project, not just Sales role
  const [hasSalesProjects, setHasSalesProjects] = useState(false);

  // Role-based default landing page redirect
  useEffect(() => {
    if (user?.role === 'AfterCare') { navigate('/aftercare', { replace: true }); return; }
    if (user?.title === 'Shade Manager' || user?.role === 'Shade Design & Sales Manager') { navigate('/projects?search=shade', { replace: true }); return; }
  }, [user, navigate]);

  // Fetch timeline gaps for eligible roles
  useEffect(() => {
    if (!isTimelineRole) return;
    getTimelineGaps()
      .then((res) => {
        const gaps = res.data;
        setTimelineGaps(gaps);
        // Show modal unless the user already deferred this session
        if (gaps && gaps.length > 0) {
          const deferred = sessionStorage.getItem('timeline_gap_deferred') === '1';
          if (!deferred) setShowTimelineModal(true);
        }
      })
      .catch((err) => {
        console.error('Failed to load timeline gaps:', err);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimelineRole]);

  // Fetch sales follow-ups for ANYONE with open proposals — shows every login
  useEffect(() => {
    getSalesFollowups()
      .then((res) => {
        const data = res.data;
        setSalesFollowups(data.followups || []);
        if (data.count > 0) {
          setHasSalesProjects(true);
          setShowSalesModal(true);
        }
      })
      .catch((err) => console.error('Failed to load sales followups:', err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Unlinked meeting notes
  const [unlinkedMeetingCount, setUnlinkedMeetingCount] = useState(0);
  const [showUnlinkedModal, setShowUnlinkedModal] = useState(false);
  const [unlinkedMeetings, setUnlinkedMeetings] = useState([]);
  const [unlinkedModalLoading, setUnlinkedModalLoading] = useState(false);
  const [linkingId, setLinkingId] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState({});

  useEffect(() => {
    getUnlinkedMeetingsCount()
      .then(res => setUnlinkedMeetingCount(res.data.count || 0))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenUnlinkedModal = () => {
    setShowUnlinkedModal(true);
    setUnlinkedModalLoading(true);
    Promise.all([
      getUnlinkedMeetings(),
      getProjects({ limit: 500 }),
    ]).then(([mRes, pRes]) => {
      setUnlinkedMeetings(mRes.data || []);
      setAllProjects(pRes.data?.projects || pRes.data || []);
    }).catch(console.error).finally(() => setUnlinkedModalLoading(false));
  };

  const handleLinkMeeting = async (meetingId) => {
    const projId = selectedProject[meetingId];
    if (!projId) return;
    setLinkingId(meetingId);
    try {
      await linkMeeting(meetingId, projId);
      setUnlinkedMeetings(prev => prev.filter(m => m.id !== meetingId));
      setUnlinkedMeetingCount(prev => Math.max(0, prev - 1));
      setSelectedProject(prev => { const next = { ...prev }; delete next[meetingId]; return next; });
    } catch (err) {
      console.error('Link meeting error:', err);
    } finally {
      setLinkingId(null);
    }
  };

  const dashPrefs = preferences.dashboard || {};
  const showFiscalYear       = dashPrefs.showFiscalYear !== false;
  const showCompanyLabor     = dashPrefs.showCompanyLabor !== false;
  const showLaborUtilization = dashPrefs.showLaborUtilization !== false;
  const showProjectOverview  = dashPrefs.showProjectOverview !== false;

  useEffect(() => {
    loadDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const basePromises = [
        getMyTasks({ raci_type: 'R', status: 'pending' }),
        getAwaitingApproval(),
        getTaskStats(),
        getProjectStats(),
      ];

      const [tasksRes, approvalRes, taskStatsRes, projectStatsRes] = await Promise.all(basePromises);

      setMyTasks(tasksRes.data.slice(0, 10));
      setAwaitingApproval(approvalRes.data.slice(0, 10));
      setTaskStats(taskStatsRes.data);
      setProjectStats(projectStatsRes.data);

      // Fetch executive summary separately for management users
      if (isMgmt) {
        getExecutiveSummary()
          .then((execRes) => setExecSummary(execRes.data))
          .catch(() => setExecError(true));
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimelineModalClose = ({ saved }) => {
    setShowTimelineModal(false);
    if (saved) {
      // Re-fetch gaps so banner count is accurate after partial save
      getTimelineGaps()
        .then((res) => setTimelineGaps(res.data))
        .catch(() => {});
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // ── Management layout ──────────────────────────────────────────────────────
  if (isMgmt) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>
            ACS Command Center
          </Typography>
          <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </Typography>
        </Box>

        {/* Executive KPI + operational panels */}
        {execError && (
          <Alert severity="warning" sx={{ mb: 4 }}>
            Executive summary data failed to load. Refresh to try again.
          </Alert>
        )}

        {execSummary ? (
          <ExecutiveDashboard execSummary={execSummary} navigate={navigate} isExec={isExec} />
        ) : !execError ? (
          /* Loading state for exec summary while other data renders */
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 3, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Loading executive summary…</Typography>
          </Box>
        ) : null}

        {/* ── Detailed Sections (collapsed by default) ── */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Button
            variant="text"
            onClick={() => setShowDetails(d => !d)}
            sx={{ textTransform: 'none', color: '#8898A4', fontSize: '0.85rem' }}
          >
            {showDetails ? '▲ Hide detailed sections' : '▼ Show detailed sections — Revenue, Tasks, Labor, Financial'}
          </Button>
        </Box>

        <Collapse in={showDetails}>

        {/* Revenue by Fiscal Year — executive access only */}
        {isExec && execSummary?.revenue_by_year && execSummary.revenue_by_year.length > 0 && (() => { const revenue_by_year = execSummary.revenue_by_year; return (
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2, mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} gutterBottom onClick={() => navigate('/sales-pipeline')} sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}>Revenue by Fiscal Year →</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Sage financials — contract value, billed, costs, and margin by fiscal year (Oct 1 – Sep 30)</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fiscal Year</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Projects</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Contract Value</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Billed</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Collected</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Outstanding AR</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Cost</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Margin %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {revenue_by_year.map((fy) => (
                    <TableRow key={fy.fiscal_year} hover>
                      <TableCell sx={{ fontWeight: 'bold' }}>{fy.fiscal_year}</TableCell>
                      <TableCell align="right">{fy.project_count}</TableCell>
                      <TableCell align="right">{fmtCurrency(fy.total_contract)}</TableCell>
                      <TableCell align="right">{fmtCurrency(fy.total_billed)}</TableCell>
                      <TableCell align="right">{fmtCurrency(fy.total_paid)}</TableCell>
                      <TableCell align="right" sx={{ color: fy.outstanding_ar > 100000 ? 'error.main' : 'text.primary', fontWeight: fy.outstanding_ar > 100000 ? 'bold' : 'normal' }}>{fmtCurrency(fy.outstanding_ar)}</TableCell>
                      <TableCell align="right">{fmtCurrency(fy.total_cost)}</TableCell>
                      <TableCell align="right" sx={{ color: fy.margin_pct >= 30 ? 'success.main' : fy.margin_pct >= 15 ? 'warning.main' : 'error.main', fontWeight: 'bold' }}>{fy.margin_pct}%</TableCell>
                    </TableRow>
                  ))}
                  {/* Totals row */}
                  <TableRow sx={{ bgcolor: 'grey.50', '& td': { fontWeight: 'bold', borderTop: 2, borderColor: 'divider' } }}>
                    <TableCell>Total</TableCell>
                    <TableCell align="right">{revenue_by_year.reduce((s, r) => s + r.project_count, 0)}</TableCell>
                    <TableCell align="right">{fmtCurrency(revenue_by_year.reduce((s, r) => s + r.total_contract, 0))}</TableCell>
                    <TableCell align="right">{fmtCurrency(revenue_by_year.reduce((s, r) => s + r.total_billed, 0))}</TableCell>
                    <TableCell align="right">{fmtCurrency(revenue_by_year.reduce((s, r) => s + r.total_paid, 0))}</TableCell>
                    <TableCell align="right">{fmtCurrency(revenue_by_year.reduce((s, r) => s + r.outstanding_ar, 0))}</TableCell>
                    <TableCell align="right">{fmtCurrency(revenue_by_year.reduce((s, r) => s + r.total_cost, 0))}</TableCell>
                    <TableCell align="right">
                      {(() => { const tc = revenue_by_year.reduce((s,r) => s+r.total_contract,0); const co = revenue_by_year.reduce((s,r) => s+r.total_cost,0); return tc > 0 ? ((tc-co)/tc*100).toFixed(1)+'%' : '—'; })()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ); })()}

        {/* My Tasks / Awaiting Approval (below watchlist) */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} onClick={() => navigate("/tasks")} sx={{ cursor: "pointer", "&:hover": { color: "primary.main", textDecoration: "underline" } }} gutterBottom>
                My Tasks →
              </Typography>
              {myTasks.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No pending tasks</Typography>
                </Box>
              ) : (
                <List sx={{ mt: 1 }}>
                  {myTasks.map((task) => (
                    <TaskItem
                      key={task.id}
                      task={task}
                      onClick={() => navigate(`/projects/${task.project_id}`)}
                    />
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} onClick={() => navigate("/tasks")} sx={{ cursor: "pointer", "&:hover": { color: "primary.main", textDecoration: "underline" } }} gutterBottom>
                Awaiting My Approval →
              </Typography>
              {awaitingApproval.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No tasks awaiting approval</Typography>
                </Box>
              ) : (
                <List sx={{ mt: 1 }}>
                  {awaitingApproval.map((task) => (
                    <TaskItem
                      key={task.id}
                      task={task}
                      onClick={() => navigate(`/projects/${task.project_id}`)}
                    />
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* WIP Labor Utilization (detail view) */}
        {showLaborUtilization && <LaborUtilizationReport />}

        {/* Fiscal Year pipeline data at the bottom */}
        {showFiscalYear && projectStats.fiscal_years && (
          <FiscalYearDashboard
            fiscalYears={projectStats.fiscal_years}
            navigate={navigate}
          />
        )}

        </Collapse>
      </Box>
    );
  }

  // ── Non-management layout (unchanged) ─────────────────────────────────────
  return (
    <Box>
      {/* Timeline gap modal — mandatory for PM / Operations roles */}
      {showTimelineModal && timelineGaps && timelineGaps.length > 0 && (
        <TimelineGapModal gaps={timelineGaps} onClose={handleTimelineModalClose} />
      )}

      {/* Sales follow-up modal — shows every login for Sales role */}
      {showSalesModal && salesFollowups && salesFollowups.length > 0 && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowSalesModal(false)}>
          <Box onClick={e => e.stopPropagation()} sx={{ bgcolor: 'white', borderRadius: 2, p: 4, maxWidth: 700, width: '90%', maxHeight: '80vh', overflow: 'auto', boxShadow: 24 }}>
            <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
              📢 Proposals Needing Follow-Up
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              You have {salesFollowups.length} proposal{salesFollowups.length !== 1 ? 's' : ''} that {salesFollowups.length !== 1 ? 'are' : 'is'} not yet accepted or in progress. Please follow up with your clients.
            </Typography>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { p: 1.5, textAlign: 'left', borderBottom: '1px solid #eee', fontSize: '0.875rem' }, '& th': { fontWeight: 700, color: '#0D2C54', bgcolor: '#f5f7fa' } }}>
              <thead>
                <tr>
                  <th>Project / Client</th>
                  <th>Job #</th>
                  <th>Stage</th>
                  <th style={{textAlign:'right'}}>Value</th>
                </tr>
              </thead>
              <tbody>
                {salesFollowups.map((f, i) => (
                  <tr key={f.id || i} style={{ cursor: 'pointer' }} onClick={() => { setShowSalesModal(false); navigate(`/projects/${f.project_id}`); }}>
                    <td>
                      <Typography variant="body2" fontWeight="bold">{f.project_name || f.local_project_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{f.client_name}</Typography>
                    </td>
                    <td><Typography variant="body2" color="primary">{f.job_number || '—'}</Typography></td>
                    <td><Typography variant="body2">{f.crm_stage}</Typography></td>
                    <td style={{textAlign:'right'}}><Typography variant="body2" fontWeight="bold">{f.proposal_price ? `$${Number(f.proposal_price).toLocaleString()}` : '—'}</Typography></td>
                  </tr>
                ))}
              </tbody>
            </Box>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">Click any row to view the project</Typography>
              <Button variant="contained" onClick={() => setShowSalesModal(false)}>Got It</Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Persistent sales follow-up banner */}
      {hasSalesProjects && salesFollowups && salesFollowups.length > 0 && !showSalesModal && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setShowSalesModal(true)}>
              View Proposals →
            </Button>
          }
        >
          📢 You have {salesFollowups.length} proposal{salesFollowups.length !== 1 ? 's' : ''} awaiting client response. Follow up today.
        </Alert>
      )}

      {/* Unlinked meeting notes banner */}
      {unlinkedMeetingCount > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleOpenUnlinkedModal}>
              Link Now →
            </Button>
          }
        >
          📝 You have {unlinkedMeetingCount} meeting note{unlinkedMeetingCount !== 1 ? 's' : ''} that {unlinkedMeetingCount !== 1 ? 'haven\'t' : 'hasn\'t'} been linked to a project.
        </Alert>
      )}

      {/* Unlinked meetings modal */}
      {showUnlinkedModal && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowUnlinkedModal(false)}>
          <Box onClick={e => e.stopPropagation()} sx={{ bgcolor: 'white', borderRadius: 2, p: 4, maxWidth: 680, width: '90%', maxHeight: '80vh', overflow: 'auto', boxShadow: 24 }}>
            <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
              📝 Unlinked Meeting Notes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Link each meeting to a project so notes appear in the Activity tab.
            </Typography>
            {unlinkedModalLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              </Box>
            ) : unlinkedMeetings.length === 0 ? (
              <Alert severity="success">All meetings are linked!</Alert>
            ) : (
              <Box>
                {unlinkedMeetings.map(m => (
                  <Box key={m.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold">{m.meeting_title || 'Untitled Meeting'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      {m.meeting_date ? new Date(m.meeting_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'No date'}
                      {m.duration_minutes ? ` · ${m.duration_minutes} min` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Select
                        size="small"
                        displayEmpty
                        value={selectedProject[m.id] || ''}
                        onChange={e => setSelectedProject(prev => ({ ...prev, [m.id]: e.target.value }))}
                        sx={{ flex: 1, fontSize: '0.85rem' }}
                      >
                        <MenuItem value=""><em>Select a project…</em></MenuItem>
                        {allProjects.map(p => (
                          <MenuItem key={p.id} value={p.id} sx={{ fontSize: '0.85rem' }}>
                            {p.primary_job_number ? `${p.primary_job_number} — ` : ''}{p.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!selectedProject[m.id] || linkingId === m.id}
                        onClick={() => handleLinkMeeting(m.id)}
                        startIcon={linkingId === m.id ? <CircularProgress size={14} color="inherit" /> : null}
                      >
                        Link
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => setShowUnlinkedModal(false)}>Close</Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Persistent warning banner — shows whenever gaps exist regardless of deferral */}
      {isTimelineRole && timelineGaps && timelineGaps.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setShowTimelineModal(true)}
            >
              Update Now →
            </Button>
          }
        >
          ⚠️ You have {timelineGaps.length} project{timelineGaps.length !== 1 ? 's' : ''} missing completion dates.
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Welcome back, {user?.name?.split(' ')[0]}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's what's happening with your projects today
        </Typography>
      </Box>

      {/* Task stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="My Tasks"
            value={taskStats.my_tasks || 0}
            icon={<AssignmentIcon fontSize="large" />}
            color="primary"
            onClick={() => navigate('/tasks')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Due Today"
            value={taskStats.due_today || 0}
            icon={<ScheduleIcon fontSize="large" />}
            color="warning"
            onClick={() => navigate('/tasks')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Overdue"
            value={taskStats.overdue || 0}
            color="error"
            onClick={() => navigate('/tasks')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Awaiting Approval"
            value={taskStats.awaiting_approval || 0}
            icon={<CheckCircleIcon fontSize="large" />}
            color="success"
            onClick={() => navigate('/tasks')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} onClick={() => navigate("/tasks")} sx={{ cursor: "pointer", "&:hover": { color: "primary.main", textDecoration: "underline" } }} gutterBottom>
              My Tasks →
            </Typography>
            {myTasks.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No pending tasks</Typography>
              </Box>
            ) : (
              <List sx={{ mt: 2 }}>
                {myTasks.map((task) => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    onClick={() => navigate(`/projects/${task.project_id}`)}
                  />
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} onClick={() => navigate("/tasks")} sx={{ cursor: "pointer", "&:hover": { color: "primary.main", textDecoration: "underline" } }} gutterBottom>
              Awaiting My Approval →
            </Typography>
            {awaitingApproval.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No tasks awaiting approval</Typography>
              </Box>
            ) : (
              <List sx={{ mt: 2 }}>
                {awaitingApproval.map((task) => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    onClick={() => navigate(`/projects/${task.project_id}`)}
                  />
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {showProjectOverview && (
        <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid #e8edf2', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: "#0D2C54" }} gutterBottom onClick={() => navigate('/projects')} sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}>
            Project Overview →
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6} sm={4} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {projectStats.active_projects || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Projects
                </Typography>
              </Box>
            </Grid>
            {[1, 2, 3, 4, 5, 6].map((phase) => (
              <Grid item xs={6} sm={4} md={2} key={phase}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold">
                    {projectStats[`phase_${phase}`] || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Phase {phase}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default Dashboard;
